import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { mvpApi } from '../services/api';
import { getQualificationResponses } from '../utils/qualificationResponses';
import { formatApiDetail } from '../utils/apiError';
import { capitalizeWords, isStrictEmail, normalizeEmail } from '../utils/formInputUtils';
import { ORIGIN_COUNTRIES } from '../utils/countries';
import { getProcedureBasePath } from '../procedures/procedureRoutes';
import { getStoredProcedureSlug } from '../procedures/ProcedureContext';
import LegalModal from '../components/LegalModal';
import LanguageSwitch from '../components/LanguageSwitch';
import AppHeader from '../components/AppHeader';
import { useApplicantFlowPaths } from '../procedures/useApplicantFlowPaths';
import './Registration.css';
import './SsoSignupFlow.css';

import { getCrewParticipantContext, crewFirstName, crewLastName } from '../utils/participantCrewContext';

const ORG_PROCEDURE_KEY = 'mvp_customer_org_procedure_slug';

function readCrewPrefill() {
  const ctx = getCrewParticipantContext();
  if (!ctx) return null;
  return {
    firstName: crewFirstName(ctx.name),
    lastName: crewLastName(ctx.name),
    email: ctx.email || '',
    originCountry: ctx.country || '',
  };
}

export default function Registration() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const paths = useApplicantFlowPaths();

  // ── Personal details ──────────────────────────────────────────────────
  const [openLegal, setOpenLegal] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [useEmailAsMain, setUseEmailAsMain] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [hasTriedContinue, setHasTriedContinue] = useState(false);

  // ── Origin country (searchable select) ────────────────────────────────
  const [originCountry, setOriginCountry] = useState('');
  const [originSearch, setOriginSearch] = useState('');
  const [originOpen, setOriginOpen] = useState(false);
  const originRef = useRef(null);

  // ── Application meta ──────────────────────────────────────────────────
  const [applicantType, setApplicantType] = useState(null);

  const formRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const applicantTypeRef = useRef(null);

  // ── Load existing user data + pre-fill ────────────────────────────────
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('userData') || '{}');
      setFirstName(stored.first_name || stored.firstName || '');
      setLastName(stored.last_name || stored.lastName || '');
      setEmail(stored.email || '');
      setOriginCountry(stored.country_of_origin || '');
      setOriginSearch(stored.country_of_origin || '');
      setApplicantType(
        stored.applicant_type === 'self'
          ? 'me'
          : stored.applicant_type === 'others'
            ? 'someone_else'
            : null
      );
      setUseEmailAsMain(Boolean(stored.email_primary));
      if (stored.date_of_birth) {
        const d = new Date(stored.date_of_birth);
        if (!Number.isNaN(d.getTime())) setDateOfBirth(d.toISOString().slice(0, 10));
      }
    } catch {}

    const crewPrefill = readCrewPrefill();
    if (crewPrefill) {
      setFirstName((v) => v || crewPrefill.firstName);
      setLastName((v) => v || crewPrefill.lastName);
      setEmail((v) => v || crewPrefill.email);
      setOriginCountry((v) => v || crewPrefill.originCountry);
      setOriginSearch((v) => v || crewPrefill.originCountry);
    }

    // Org customers: program was chosen at org signup - apply stored slug only.
    try {
      const orgProcedure = sessionStorage.getItem(ORG_PROCEDURE_KEY);
      if (orgProcedure) {
        sessionStorage.setItem('mvp_procedure_slug', orgProcedure);
      }
    } catch {}
  }, []);

  // ── Close origin dropdown on outside click ─────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (originRef.current && !originRef.current.contains(e.target)) {
        setOriginOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Computed ──────────────────────────────────────────────────────────
  const hasInvalidEmail = Boolean(normalizeEmail(email) && !isStrictEmail(email));
  const filteredOrigin = ORIGIN_COUNTRIES.filter((c) =>
    c.toLowerCase().startsWith(originSearch.toLowerCase())
  );

  const isContinueDisabled =
    loading ||
    !firstName.trim() ||
    !lastName.trim() ||
    !applicantType ||
    hasInvalidEmail;

  // ── Helpers ───────────────────────────────────────────────────────────
  const focusAndScroll = (target) => {
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => target.focus({ preventScroll: true }), 200);
  };

  const getFirstInvalidField = () => {
    if (!firstName.trim()) return firstNameRef.current;
    if (!lastName.trim()) return lastNameRef.current;
    if (!applicantType) return applicantTypeRef.current;
    if (hasInvalidEmail) return emailRef.current;
    return null;
  };

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasTriedContinue(true);
    setError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setError(t('ssoSignup.errors.nameRequired', 'First and last name are required.'));
      focusAndScroll(getFirstInvalidField());
      return;
    }
    if (!applicantType) {
      setError(t('ssoSignup.errors.forRequired', 'Select who this application is for.'));
      focusAndScroll(getFirstInvalidField());
      return;
    }
    const normalizedEmail = normalizeEmail(email);
    if (normalizedEmail && !isStrictEmail(normalizedEmail)) {
      setEmailError(t('ssoSignup.errors.invalidEmail', 'Please enter a valid email address.'));
      focusAndScroll(getFirstInvalidField());
      return;
    }

    setLoading(true);
    try {
      const qualificationResponses = getQualificationResponses();
      const res = await mvpApi.updatePersonalDetails({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: normalizedEmail || null,
        date_of_birth: dateOfBirth || null,
        country_of_origin: originCountry || null,
        applicant_type: applicantType === 'me' ? 'self' : 'others',
        application_type: 'individual',
        family_count: null,
        email_primary: useEmailAsMain,
        consent_updates: true,
        qualificationResponses: qualificationResponses || null,
      });
      const user = res.data?.user;
      if (user) localStorage.setItem('userData', JSON.stringify(user));

      // Org program or procedure landing page (non-org direct links only).
      let slugToUse = null;
      try {
        slugToUse = sessionStorage.getItem(ORG_PROCEDURE_KEY);
      } catch {}
      if (!slugToUse) slugToUse = getStoredProcedureSlug();
      if (slugToUse) {
        try { sessionStorage.setItem('mvp_procedure_slug', slugToUse); } catch {}
        const base = getProcedureBasePath(slugToUse);
        if (base) {
          navigate(`${base}/success`);
          return;
        }
      }
      // Fallback: Spain regularization success (or generic paths.success if set via context)
      navigate(paths.success);
    } catch (err) {
      const fromApi = formatApiDetail(err.response?.data?.detail);
      setError(fromApi || err.message || t('ssoSignup.errors.saveFailed', 'Could not save. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // ── Enter-key tab-advance ──────────────────────────────────────────────
  const getEnterNavigationTargets = () => {
    const root = formRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
    )).filter((el) => !el.disabled && el.getAttribute('aria-hidden') !== 'true' && el.tabIndex >= 0);
  };

  const handleEnterAdvance = (e) => {
    if (e.key !== 'Enter') return;
    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.target instanceof HTMLTextAreaElement) return;
    const targets = getEnterNavigationTargets();
    const idx = targets.indexOf(e.target);
    if (idx === -1) return;
    e.preventDefault();
    const next = targets[idx + 1];
    if (next) { next.focus(); return; }
    formRef.current?.requestSubmit();
  };

  const handleDisabledContinueAttempt = () => {
    if (!isContinueDisabled) return;
    setHasTriedContinue(true);
    focusAndScroll(getFirstInvalidField());
  };

  // ── UI choices ────────────────────────────────────────────────────────
  const choicesApplicant = [
    { value: 'me', label: t('ssoSignup.forMe'), icon: 'ri-user-3-line' },
    { value: 'someone_else', label: t('ssoSignup.forSomeoneElse'), icon: 'ri-user-shared-2-line' },
  ];

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="reg-page">
      <AppHeader rightSlot={<LanguageSwitch />} />

      <main className="reg-main">
        {/* Progress */}
        <div className="reg-progress">
          <div />
          <div />
          <div />
        </div>
        <div className="reg-step-row">
          <span>YOUR DETAILS</span>
          <span className="reg-time">{'< 2 min'}</span>
        </div>

        <h1 className="sso-title">
          <Trans i18nKey="ssoSignup.detailsTitle" components={{ accent: <span className="sso-title-accent" /> }} />
        </h1>
        <p className="sso-subdesc">{t('ssoSignup.detailsSubtitle')}</p>

        <form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleEnterAdvance} className="reg-form">

          {/* ── Section 1: Name ─────────────────────────────────────── */}
          <div className="reg-section-label">
            <i className="ri-user-line" aria-hidden /> About you
          </div>

          <div className="reg-grid-2">
            <div>
              <label className="reg-label">{t('ssoSignup.firstName')}</label>
              <input
                ref={firstNameRef}
                type="text"
                className={`reg-input ${hasTriedContinue && !firstName.trim() ? 'reg-input--invalid' : ''}`}
                value={firstName}
                onChange={(e) => setFirstName(capitalizeWords(e.target.value))}
                required
              />
            </div>
            <div>
              <label className="reg-label">{t('ssoSignup.lastName')}</label>
              <input
                ref={lastNameRef}
                type="text"
                className={`reg-input ${hasTriedContinue && !lastName.trim() ? 'reg-input--invalid' : ''}`}
                value={lastName}
                onChange={(e) => setLastName(capitalizeWords(e.target.value))}
                required
              />
            </div>
          </div>

          <div>
            <label className="reg-label">
              {t('ssoSignup.verifyEmailOptional')} <span className="reg-label-optional">{t('ssoSignup.optional')}</span>
            </label>
            <input
              ref={emailRef}
              type="email"
              className={`reg-input ${hasInvalidEmail ? 'reg-input--invalid' : ''}`}
              value={email}
              onChange={(e) => {
                const next = e.target.value;
                setEmail(next);
                const normalized = normalizeEmail(next);
                setEmailError(normalized && !isStrictEmail(normalized) ? t('ssoSignup.errors.invalidEmail', 'Please enter a valid email address.') : '');
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData('text') || '';
                const next = `${email}${pasted}`;
                const normalized = normalizeEmail(next);
                setEmailError(normalized && !isStrictEmail(normalized) ? t('ssoSignup.errors.invalidEmail', 'Please enter a valid email address.') : '');
              }}
            />
            {emailError ? <p className="reg-error">{emailError}</p> : null}
            <p className="reg-help">{t('ssoSignup.updatesOrDocs')}</p>
            {email.trim().length > 0 && (
              <button type="button" className="reg-checkbox-row" onClick={() => setUseEmailAsMain((v) => !v)}>
                <span className={`reg-checkbox ${useEmailAsMain ? 'checked' : ''}`} />
                <span>{t('ssoSignup.emailAsMain')}</span>
              </button>
            )}
          </div>

          <div className="reg-divider" />

          {/* ── Section 2: Origin country ────────────────────────────── */}
          <div className="reg-section-label">
            <i className="ri-map-pin-2-line" aria-hidden /> Where are you from?
          </div>

          <div ref={originRef} className="reg-origin-wrap">
            <label className="reg-label">Country of origin <span className="reg-label-optional">optional</span></label>
            <div className="reg-origin-field" onClick={() => setOriginOpen((v) => !v)}>
              <input
                type="text"
                className="reg-input reg-origin-input"
                placeholder="Search country…"
                value={originSearch}
                onChange={(e) => {
                  setOriginSearch(e.target.value);
                  setOriginCountry('');
                  setOriginOpen(true);
                }}
                onFocus={() => setOriginOpen(true)}
                autoComplete="off"
              />
              <span className="reg-origin-arrow" aria-hidden>
                <i className={`ri-arrow-${originOpen ? 'up' : 'down'}-s-line`} />
              </span>
            </div>
            {originOpen && filteredOrigin.length > 0 && (
              <ul className="reg-origin-list" role="listbox">
                {filteredOrigin.slice(0, 80).map((c) => (
                  <li
                    key={c}
                    role="option"
                    className={`reg-origin-item ${originCountry === c ? 'active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setOriginCountry(c);
                      setOriginSearch(c);
                      setOriginOpen(false);
                    }}
                  >
                    {c}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="reg-divider" />

          {/* ── Section 3: Who is this for ────────────────────────────── */}
          <div className="reg-section-label">
            <i className="ri-team-line" aria-hidden /> About your application
          </div>

          <div>
            <p className="reg-group-title">{t('ssoSignup.forQuestion')}</p>
            <div className="reg-choice-row">
              {choicesApplicant.map(({ value, label, icon }) => {
                const active = applicantType === value;
                return (
                  <button
                    ref={value === 'me' ? applicantTypeRef : null}
                    key={value}
                    type="button"
                    className={`sso-choice-btn ${active ? 'sso-choice-btn--active' : ''}`}
                    onClick={() => setApplicantType(value)}
                  >
                    <span className="sso-choice-icon"><i className={icon} aria-hidden /></span>
                    <span>{label}</span>
                    {active && (
                      <span className="sso-choice-check"><i className="ri-check-line" aria-hidden /></span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {error && <p className="reg-error">{error}</p>}

          <div className="reg-btn-primary-wrap" onClick={handleDisabledContinueAttempt}>
            <button
              type="submit"
              className="btn-primary reg-continue-btn"
              disabled={isContinueDisabled}
            >
              {loading
                ? t('ssoSignup.saving')
                : t('ssoSignup.continue')}
            </button>
          </div>

          <p className="reg-details-terms">
            <Trans
              i18nKey="ssoSignup.detailsContinueTerms"
              components={{
                terms: <button type="button" className="reg-details-terms-link" onClick={() => setOpenLegal('terms')} />,
                privacy: <button type="button" className="reg-details-terms-link" onClick={() => setOpenLegal('privacy')} />,
                dpa: <button type="button" className="reg-details-terms-link" onClick={() => setOpenLegal('data')} />,
              }}
            />
          </p>
        </form>
      </main>

      <LegalModal type={openLegal || 'terms'} open={openLegal !== null} onClose={() => setOpenLegal(null)} />
    </div>
  );
}
